/* eslint-disable react/jsx-fragments */
import React from "react";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import Collapse from "reactstrap/lib/Collapse";
import Navbar from "reactstrap/lib/Navbar";
import NavbarToggler from "reactstrap/lib/NavbarToggler";
import NavbarBrand from "reactstrap/lib/NavbarBrand";
import Nav from "reactstrap/lib/Nav";
import HamburgerMenuIcon from "@druva-apps/orion-icons/lib/HamburgermenuGreyOutline";
import DruvaLogo from "@druva-apps/orion-icons/lib/DruvaGreyFilled";

import {
  gatherClasses,
  gatherConditionalProps,
  isFunction
} from "../../lib/utils";
import {
  renderLeftComponents,
  renderRightComponents
} from "../../lib/headerbarUtils";
import Floater from "../floater/floater";
import HeaderbarResponsive from "./headerbarResponsive";
import { OrionContext } from "../orionContext/orionContext";
import DellHeaderbar from "./dellHeaderbar";
import "./headerbar.scss";
import "./msp-provider.scss";

const ProductName = ({ children, className }) => (
  <span className={gatherClasses(null, "oui-product-name", className)}>
    {children}
  </span>
);

ProductName.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};

const InclinedSeparatorSm = () => (
  <div className="oui-headerbar-separator oui-separator-sm oui-separator-inclined oui-ml-2 oui-mr-2" />
);

const ServiceName = ({ children, className }) => (
  <span className={gatherClasses(null, "oui-service-name", className)}>
    {children}
  </span>
);

ServiceName.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};

class Headerbar extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      collapsed: true
    };
  }

  toggleNavbar = () => {
    const { collapsed } = this.state;
    this.setState({
      collapsed: !collapsed
    });
  };

  onHeaderbarClick = e => {
    const { onClick } = this.props;
    const { collapsed } = this.state;

    if (!collapsed) {
      this.toggleNavbar();
    }

    onClick(e);
  };

  render() {
    const {
      consoleConfig,
      mspConfig,
      brand,
      left,
      right,
      dataTestId,
      fixed,
      onGlobalMenuClick,
      isGlobalMenuActive
    } = this.props;
    const { collapsed } = this.state;
    const { isDellApplication, dellDoubleHeader } = this.context || {};

    return (
      <React.Fragment>
        {isFunction(isDellApplication) &&
          isDellApplication() &&
          dellDoubleHeader && (
            <DellHeaderbar
              consoleConfig={consoleConfig}
              logo={brand ? brand.logoUrl : null}
              portalName={brand ? brand.portalName : null}
              portalLogo={brand ? brand.portalLogo : null}
              fixed={fixed}
            />
          )}

        <Navbar
          data-testid={dataTestId}
          color="dark"
          dark
          expand="lg"
          fixed={fixed ? "top" : null}
          onClick={this.onHeaderbarClick}
          className={gatherClasses(
            this.props,
            "oui-headerbar",
            dellDoubleHeader && "oui-double-header-support"
          )}
        >
          <div className="oui-headerbar-brand-wrapper">
            {isFunction(onGlobalMenuClick) && (
              <div
                className={gatherClasses(
                  null,
                  "oui-left-nav-toggler",
                  isGlobalMenuActive && "oui-active"
                )}
              >
                <div className="oui-hamburger-icon">
                  <HamburgerMenuIcon
                    muted={false}
                    clickable={false}
                    inheritFontColor
                    onClick={onGlobalMenuClick}
                    className="oui-hamburger-svg"
                  />
                </div>
              </div>
            )}

            {/* Render Druva logo in case of no Dell */}
            {(!isFunction(isDellApplication) || !isDellApplication()) && (
              <a
                className={gatherClasses(
                  null,
                  "oui-headerbar-druva-logo",
                  !isFunction(onGlobalMenuClick) && "oui-pl-3",
                  consoleConfig && consoleConfig.isActive && "oui-active",
                  consoleConfig &&
                    (consoleConfig.onClick || consoleConfig.href) &&
                    "oui-hoverable"
                )}
                href={consoleConfig && consoleConfig.href}
                onClick={consoleConfig && consoleConfig.onClick}
                target={consoleConfig && consoleConfig.target}
              >
                <DruvaLogo
                  inheritFontColor
                  width={24}
                  height={24}
                  muted={false}
                />
              </a>
            )}

            {/* Render MSP Container */}
            {mspConfig && (
              <React.Fragment>
                <div className="oui-headerbar-msp-container oui-pl-3 oui-pr-2">
                  {mspConfig.logoUrl && (
                    <img
                      src={mspConfig.logoUrl}
                      className="oui-msp-logo"
                      alt={mspConfig.mspName}
                    />
                  )}

                  <a
                    className={gatherClasses(
                      null,
                      "oui-msp-name oui-pl-2",
                      mspConfig.isActive && "oui-active",
                      (mspConfig.href || isFunction(mspConfig.onClick)) &&
                        "oui-hoverable"
                    )}
                    href={mspConfig.href}
                    onClick={mspConfig.onClick}
                    target={mspConfig.target}
                  >
                    {mspConfig.mspName}
                  </a>
                </div>
                <div className="oui-headerbar-separator oui-separator-lg" />
              </React.Fragment>
            )}

            {/* Dell EMC Container */}
            {isFunction(isDellApplication) &&
              isDellApplication() &&
              !dellDoubleHeader && (
                <React.Fragment>
                  <div className="oui-headerbar-dell-container oui-pl-3 oui-pr-2">
                    <a
                      href={consoleConfig && consoleConfig.href}
                      onClick={consoleConfig && consoleConfig.onClick}
                      target={consoleConfig && consoleConfig.target}
                    >
                      <img
                        src={brand.logoUrl}
                        alt={brand.name}
                        style={{ height: "19px" }}
                      />
                    </a>
                  </div>
                  <div className="oui-headerbar-separator oui-separator-lg" />
                </React.Fragment>
              )}

            {/* Render Brand Container */}
            {brand && (isFunction(brand.renderComponent) || brand.name) && (
              <div className="oui-headerbar-brand-container">
                {isFunction(brand.renderComponent) ? (
                  brand.renderComponent(
                    brand,
                    "oui-active",
                    gatherClasses(
                      null,
                      "oui-navbar-brand",
                      "oui-brand-name",
                      "oui-clickable"
                    ),
                    { ProductName, Separator: InclinedSeparatorSm, ServiceName }
                  )
                ) : (
                  <NavbarBrand
                    className={gatherClasses(
                      null,
                      brand.isActive ? "oui-active" : null,
                      isFunction(brand.onClick) || !isEmpty(brand.href)
                        ? "oui-brand-name oui-clickable"
                        : "oui-brand-name"
                    )}
                    {...gatherConditionalProps(
                      brand,
                      "href",
                      "target",
                      "disabled",
                      "onClick"
                    )}
                  >
                    {brand.name}
                  </NavbarBrand>
                )}
              </div>
            )}
          </div>
          {left || right ? (
            <React.Fragment>
              {collapsed === false && (
                <div className="oui-position-fixed oui-separator-container">
                  <div className="oui-separator-line" />
                </div>
              )}
              <NavbarToggler
                className={gatherClasses(
                  null,
                  collapsed === false ? "oui-active" : null
                )}
                onClick={this.toggleNavbar}
                aria-label="headerbar toggler"
              />

              <Collapse navbar>
                {left ? (
                  <Nav navbar className="oui-hb-left">
                    {renderLeftComponents(left, this.context)}
                  </Nav>
                ) : null}

                {right ? (
                  <Nav className="oui-ml-auto oui-hb-right" navbar>
                    {renderRightComponents(right, this.context)}
                  </Nav>
                ) : null}
              </Collapse>
            </React.Fragment>
          ) : null}
        </Navbar>
        <Floater
          position="right"
          show={!collapsed}
          overlay
          onOverlayClick={this.toggleNavbar}
        >
          <HeaderbarResponsive
            left={left}
            right={right}
            onItemClick={this.toggleNavbar}
          />
        </Floater>
      </React.Fragment>
    );
  }
}

Headerbar.contextType = OrionContext;

Headerbar.propTypes = {
  fixed: PropTypes.bool,
  onGlobalMenuClick: PropTypes.func,
  isGlobalMenuActive: PropTypes.bool,
  onClick: PropTypes.func,
  brand: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    logoUrl: PropTypes.string,
    renderComponent: PropTypes.func,
    isActive: PropTypes.bool,
    href: PropTypes.string,
    portalLogo: PropTypes.string,
    portalName: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
  }),
  consoleConfig: PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
    target: PropTypes.string,
    isActive: PropTypes.bool
  }),
  mspConfig: PropTypes.shape({
    logoUrl: PropTypes.string,
    mspName: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    target: PropTypes.string,
    isActive: PropTypes.bool
  }),
  left: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.string,
      data: PropTypes.any
    })
  ),
  right: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.string,
      data: PropTypes.any
    })
  ),
  className: PropTypes.string,
  hideHamburgerIcon: PropTypes.bool,
  dataTestId: PropTypes.string
};

Headerbar.defaultProps = {
  fixed: true,
  isGlobalMenuActive: false,
  onClick: () => {},
  hideHamburgerIcon: false,
  dataTestId: "orion-headerbar"
};

Headerbar.displayName = "OrionHeaderbar";

export default Headerbar;
